/**
 * Curve calculation for arrow rendering
 *
 * Handles Bezier curve control point calculation and arrow head positioning
 * Refactored to use named constants and geometry utilities
 */

import { v3 } from "@alchemiesolns/maths";
import { CurveProps, HeadPointProps } from "../types";
import { calculateControlPoint, determineLeftSide } from "./geometry";
import {
  CURVE_CONSTANTS,
  ARROW_HEAD_CONSTANTS,
} from "./constants";
import { calculateArrowEndpoints } from "../calculators";

/**
 * Arrow curve calculation result
 */
export interface CurvePoints {
  /** Start position of curve */
  start: number[];
  /** First control point */
  cp1: number[];
  /** Second control point */
  cp2: number[];
  /** End position of curve */
  end: number[];
  /** Arrow head configuration */
  head: {
    /** Arrow head position */
    pos: number[];
    /** True if arrow head is on left side */
    side: boolean;
  };
}

/**
 * CurveMath class for arrow curve calculations
 */
export class CurveMath {
  /**
   * Calculate all curve points including control points and arrow head
   *
   * @param props - Curve configuration properties
   * @returns Complete curve point data
   */
  public static calculatePoints(props: CurveProps): CurvePoints {
    // Use new calculator system to get endpoints and directions
    const { startPos, endPos, startDir, p3, endDir } = calculateArrowEndpoints({
      source: props.source,
      destination: props.destination,
      cameraDir: props.cameraDir,
      isDestAdjacent: props.isDestAdjacent,
      type: props.type,
      format: props.format,
      overrideDest: props.overrideDest,
    });

    // Calculate distance for control point scaling
    const distance = v3.distance(startPos, endPos);

    // Calculate control points using geometry utilities
    const cp1 = calculateControlPoint(
      startPos,
      startDir,
      CURVE_CONSTANTS.START_CONTROL_POINT_DISTANCE,
      CURVE_CONSTANTS.CONTROL_POINT_SCALAR
    );

    const cp2 = calculateControlPoint(
      p3,
      endDir,
      distance,
      CURVE_CONSTANTS.CONTROL_POINT_SCALAR
    );

    // Calculate arrow head position
    const head = this.calculateArrowHeadPosition({
      position: p3,
      dir: endDir,
      isDestAdjacent: props.isDestAdjacent,
    });

    // Determine which side of the curve the arrow head should be on
    const perpRef = v3.crossVectors(props.cameraDir, endDir);
    const left = determineLeftSide(
      head.pos,
      perpRef,
      startPos,
      ARROW_HEAD_CONSTANTS.SIDE_CALCULATION_OFFSET
    );

    return {
      start: startPos,
      cp1: cp1,
      cp2: cp2,
      end: p3,
      head: {
        pos: head.pos,
        side: left,
      },
    };
  }

  /**
   * Calculate arrow head position
   *
   * @param props - Arrow head positioning properties
   * @returns Arrow head position
   */
  public static calculateArrowHeadPosition({
    position,
    dir,
    isDestAdjacent,
  }: HeadPointProps): { pos: number[] } {
    const offset = isDestAdjacent
      ? ARROW_HEAD_CONSTANTS.ADJACENT_HEAD_OFFSET
      : ARROW_HEAD_CONSTANTS.NON_ADJACENT_HEAD_OFFSET;

    return {
      pos: v3.addScaledVector(position, dir, offset),
    };
  }
}
